import { useState, useReducer, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Row, Col } from "react-bootstrap";
import { cloneDeep } from "lodash";

// Hook
import useMessage from "hooks/useMessage";
import usePageContext from "hooks/usePageContext";

// Slice
import { uiActions, selectShowModalSpinner } from "stores/slice/ui";

// UI
import SpinnerForModal from "ui/SpinnerForModal";

// Component
import MessageBoxGroup from "components/MessageBoxGroup";
import Input from "components/Input";
import TooltipDialog from "components/TooltipDialog";
import Button from "components/Button";
import ModalContainer from "components/ModalContainer";
import AccessPrivilege, { accessLevelType } from "components/AccessPrivilege";

// Model
import SmsConfigModel from "../models/SmsConfig";

// Store
import smsConfigStore from "../stores/smsConfig";

// Const
import { organizationType } from "const/nuclias/organization";
import { smsConfigField } from "../const";

// Controller
import controller from "../controller";

const AddOrEditSmsAccountModal = props => {
  const {
    modalStatus,
    changeModalStatus,
    selectedOrg,
    managedSiteTagList,
    managedSiteList,
    selectedSmsConfig = null,
    successCallback
  } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Slice
  const showModalSpinner = useSelector(selectShowModalSpinner);

  // Hook
  const { getAccessLevel } = usePageContext();
  const {
    messages,
    messagesType,
    resetMessages,
    setErrorMessage
  } = useMessage();

  // State
  const [smsConfigState, smsConfigDispatch] = useReducer(
    smsConfigStore.reducer,
    smsConfigStore.defaultState
  );
  const [siteTagList, setSiteTagList] = useState([]);
  const [siteList, setSiteList] = useState([]);

  // Method
  const init = useCallback(() => {
    const siteTagList = controller.getDefaultSiteTagList(false, managedSiteTagList, selectedSmsConfig);
    const siteList = controller.getDefaultSiteList(false, managedSiteList, selectedSmsConfig);
    setSiteTagList(siteTagList);
    setSiteList(siteList);

    smsConfigDispatch({
      type: smsConfigStore.action.initializeState,
      payload: SmsConfigModel.getSmsConfigState(
        selectedSmsConfig,
        siteTagList, siteList,
        accessLevelType, getAccessLevel()
      )
    });
  }, [selectedSmsConfig]);

  const changeField = (field, value) => {
    const getMethodName = (str) => {
      const firstLetter = str.charAt(0);
      const firstLetterCap = firstLetter.toUpperCase();
      const remainingLetters = str.slice(1);

      return firstLetterCap + remainingLetters;
    }

    const updatedField = cloneDeep(smsConfigState[field]);
    const methodName = `change${getMethodName(field)}`;
    SmsConfigModel[methodName](updatedField, value, smsConfigState);

    smsConfigDispatch({
      type: smsConfigStore.action[methodName],
      payload: updatedField
    });
  }

  const changeAccessLevel = (accessLevel) => {
    changeField(smsConfigField.accessLevel, accessLevel);

    const updatedSiteTagList = cloneDeep(siteTagList);
    const updatedSiteList = cloneDeep(siteList);
    updatedSiteTagList.forEach(siteTagItem => siteTagItem.isActive = false);
    updatedSiteList.forEach(siteItem => siteItem.isActive = false);

    if (!accessLevel) {
      changeField(smsConfigField.siteTagOrSite, null);
    }

    if (accessLevel?.accessLevel === accessLevelType.tag && updatedSiteTagList.length > 0) {
      updatedSiteTagList[0].isActive = true;
      changeField(smsConfigField.siteTagOrSite, updatedSiteTagList[0]);
    }

    if (accessLevel?.accessLevel === accessLevelType.site && updatedSiteList.length > 0) {
      updatedSiteList[0].isActive = true;
      changeField(smsConfigField.siteTagOrSite, updatedSiteList[0]);
    }

    setSiteTagList(updatedSiteTagList);
    setSiteList(updatedSiteList);
  }

  const checkForm = () => {
    if (
      (smsConfigState.accessLevel.value === accessLevelType.tag || smsConfigState.accessLevel.value === accessLevelType.site) &&
      !smsConfigState.siteTagOrSite.value
    ) {
      return false;
    }


    let state = true;
    for (const fieldKey in smsConfigState) {
      if (fieldKey === 'accessLevel' || fieldKey === 'siteTagOrSite') {
        continue;
      }

      const { valid } = smsConfigState[fieldKey];
      if (!valid) {
        state = false;
      }
    }

    return state;
  }

  const addOrSave = async () => {
    try {
      const selectedSmsConfigId = selectedSmsConfig?.rawData.id || null;
      const response = await controller.addOrSaveSmsConfig(selectedOrg.id, selectedSmsConfigId, smsConfigState);

      if (response.error) {
        throw response.error;
      }

      dispatch(uiActions.hideModalSpinner());
      resetMessages();
      changeModalStatus(modalStatus.addOrEdit.type, false);
      successCallback();
    } catch (error) {
      setErrorMessage(error.code);
      dispatch(uiActions.hideModalSpinner());
    }
  }

  // Side effect
  useEffect(() => {
    if (modalStatus.addOrEdit.status) {
      resetMessages();
      dispatch(uiActions.showModalSpinner());
      init();
      setTimeout(() => {
        dispatch(uiActions.hideModalSpinner());
      }, 1000);
    }
  }, [dispatch, init, modalStatus.addOrEdit.status]);

  return (
    <ModalContainer
      modalWidthType="modal-500px"
      openModal={modalStatus.addOrEdit.status}
      closeModal={() => changeModalStatus(modalStatus.addOrEdit.type, false)}
    >
      {showModalSpinner && <SpinnerForModal />}
      <div className="header">
        <div className="title">
          {!selectedSmsConfig && t('87ea302945')}
          {selectedSmsConfig && t('ce1922f78c')}
        </div>
      </div>
      <div className="body">
        <MessageBoxGroup
          messages={messages}
          ErrorMessagesType={messagesType.error.server}
          onClose={resetMessages}
        />
        <div>
          <Row className="mb-2">
            <Col sm={6}>
              {/* Name */}
              <div className="modal-form-title required">{t('49ee308734')}</div>
              <Input
                type="text"
                placeholder="7563cde047"
                isInvalid={smsConfigState.name.dirty && !smsConfigState.name.valid}
                value={smsConfigState.name.value}
                onChange={e => changeField(smsConfigField.name, e.target.value)}
              />
            </Col>
            <Col sm={6}>
              {/* Twilio account SID */}
              <div className="modal-form-title required">
                {t('b8cb1a8790')}
              </div>
              <Input
                type="text"
                placeholder="7563cde047"
                isInvalid={smsConfigState.twilioAccountSid.dirty && !smsConfigState.twilioAccountSid.valid}
                value={smsConfigState.twilioAccountSid.value}
                onChange={e => changeField(smsConfigField.twilioAccountSid, e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col sm={6}>
              {/* Twilio auth token */}
              <div className="modal-form-title required">{t('c4a1359930')}</div>
              <Input
                type="text"
                placeholder="ca5977f4f2"
                isInvalid={smsConfigState.twilioAuthToken.dirty && !smsConfigState.twilioAuthToken.valid}
                value={smsConfigState.twilioAuthToken.value}
                onChange={e => changeField(smsConfigField.twilioAuthToken, e.target.value)}
              />
            </Col>
            <Col sm={6}>
              {/* Twilio from number */}
              <div className="modal-form-title required">
                {t('5f1b1bed2c')}
              </div>
              <Input
                type="text"
                placeholder="+1234567890"
                isInvalid={smsConfigState.twilioFromNumber.dirty && !smsConfigState.twilioFromNumber.valid}
                value={smsConfigState.twilioFromNumber.value}
                onChange={e => changeField(smsConfigField.twilioFromNumber, e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col sm={6}>
              {/* Resend after (minutes) */}
              <div className="d-flex">
                <div className="modal-form-title required me-3">
                  {t('2841a38cbf')} ({t('26fba427f3')})
                </div>
                <TooltipDialog title="tooltip_sms_resend_after" />
              </div>
              <Input
                type="number"
                placeholder="0-60"
                isInvalid={smsConfigState.resendAfter.dirty && !smsConfigState.resendAfter.valid}
                value={smsConfigState.resendAfter.value}
                onChange={e => changeField(smsConfigField.resendAfter, e.target.value)}
              />
            </Col>
            <Col sm={6}>
              {/* Maximum resend (times) */}
              <div className="d-flex">
                <div className="modal-form-title required me-3">
                  {t('c37d6da05a')} ({t('26fba427f3')})
                </div>
                <TooltipDialog title="tooltip_sms_max_resend" />
              </div>
              <Input
                type="number"
                placeholder="0-60"
                isInvalid={smsConfigState.maximumResend.dirty && !smsConfigState.maximumResend.valid}
                value={smsConfigState.maximumResend.value}
                onChange={e => changeField(smsConfigField.maximumResend, e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col sm={6}>
              {/* Maximum request (times) */}
              <div className="d-flex">
                <div className="modal-form-title required me-3">
                  {t('3923abc37b')} ({t('26fba427f3')})
                </div>
                <TooltipDialog title="tooltip_sms_max_request" />
              </div>
              <Input
                type="number"
                placeholder="0-60"
                isInvalid={smsConfigState.maximumRequest.dirty && !smsConfigState.maximumRequest.valid}
                value={smsConfigState.maximumRequest.value}
                onChange={e => changeField(smsConfigField.maximumRequest, e.target.value)}
              />
            </Col>
            <Col sm={6}>
              {/* Denial period (minutes) */}
              <div className="d-flex">
                <div className="modal-form-title required me-3">
                  {t('7c3612e44a')} ({t('26fba427f3')})
                </div>
                <TooltipDialog title="tooltip_sms_denial_period" />
              </div>
              <Input
                type="number"
                placeholder="0-1440"
                isInvalid={smsConfigState.denialPeriod.dirty && !smsConfigState.denialPeriod.valid}
                value={smsConfigState.denialPeriod.value}
                onChange={e => changeField(smsConfigField.denialPeriod, e.target.value)}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              {/* OTP expires in (minutes) */}
              <div className="d-flex">
                <div className="modal-form-title required me-3">
                  {t('ed74a86521')} ({t('26fba427f3')})
                </div>
                <TooltipDialog title="tooltip_sms_expires_in" />
              </div>
              <Input
                type="number"
                placeholder="5-30"
                isInvalid={smsConfigState.otpExpiresIn.dirty && !smsConfigState.otpExpiresIn.valid}
                value={smsConfigState.otpExpiresIn.value}
                onChange={e => changeField(smsConfigField.otpExpiresIn, e.target.value)}
              />
            </Col>
            <Col sm={6}>
              {/* Allowed phone prefix */}
              <div className="d-flex">
                <div className="modal-form-title required me-3">
                  {t('6bbce8f4aa')}
                </div>
                <TooltipDialog title="tooltip_sms_allowed_phone_prefix" />
              </div>
              <Input
                type="text"
                placeholder="[*|,|0-9]" isInvalid={smsConfigState.allowedPhonePrefix.dirty && !smsConfigState.allowedPhonePrefix.valid}
                value={smsConfigState.allowedPhonePrefix.value}
                onChange={e => changeField(smsConfigField.allowedPhonePrefix, e.target.value)}
              />
            </Col>
          </Row>
        </div>

        {/* Access privilege */}
        {getAccessLevel() !== organizationType.site && (
          <AccessPrivilege
            accessLevel={getAccessLevel()}
            seletedAccessLevel={smsConfigState.accessLevel.value}
            onChangeSelectedLevelItem={accessLevel => changeAccessLevel(accessLevel)}
            onChangeSelectedItem={siteTagOrSite => changeField(smsConfigField.siteTagOrSite, siteTagOrSite)}
            siteTagList={siteTagList}
            siteList={siteList}
          />
        )}
      </div>
      <div className="footer">
        <Button
          label="ea4788705e"
          className="btn-cancel"
          onClick={() => changeModalStatus(modalStatus.addOrEdit.type, false)}
        />
        <Button
          label="c9cc8cce24"
          className="btn-submit"
          disabled={!checkForm()}
          onClick={() => addOrSave()}
        />
      </div>
    </ModalContainer>
  );
};

export default AddOrEditSmsAccountModal;
